import { useEffect, useRef, useCallback } from 'react';

/**
 * TapTooltipChart — Sep 2026 phone-view helper for Recharts charts.
 *
 * Recharts 3 only shows its Tooltip on mouse-move or touch-MOVE; a plain
 * finger tap does nothing and phones have no hover. This wrapper turns a
 * tap / tap-and-hold into the same tooltip: on touchstart it replays the
 * touch point as a synthetic `mousemove` (which Recharts already handles),
 * and a tap anywhere outside the chart replays `mouseout` so the tooltip
 * closes. Desktop mouse behaviour is untouched.
 *
 * `touch-action: pan-y` keeps vertical page scrolling working while a
 * horizontal finger drag scrubs across the data points.
 */
export default function TapTooltipChart({ children, className = '', style, testId }) {
  const ref = useRef(null);

  const replayMouse = useCallback((type, x, y, target) => {
    const el = target || ref.current;
    if (!el) return;
    el.dispatchEvent(new MouseEvent(type, {
      bubbles: true,
      cancelable: true,
      clientX: x,
      clientY: y,
      relatedTarget: type === 'mouseout' ? document.body : null,
    }));
  }, []);

  const onTouchStart = useCallback((e) => {
    const t = e.touches?.[0];
    if (!t) return;
    replayMouse('mousemove', t.clientX, t.clientY, e.target);
  }, [replayMouse]);

  useEffect(() => {
    const onDocTouch = (e) => {
      const root = ref.current;
      if (!root || root.contains(e.target)) return;
      const wrapper = root.querySelector('.recharts-wrapper');
      if (wrapper) replayMouse('mouseout', 0, 0, wrapper);
    };
    document.addEventListener('touchstart', onDocTouch, { passive: true });
    return () => document.removeEventListener('touchstart', onDocTouch);
  }, [replayMouse]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ touchAction: 'pan-y', ...style }}
      onTouchStart={onTouchStart}
      data-testid={testId}
      data-no-tap-tip
    >
      {children}
    </div>
  );
}
